import { useEffect, useId, useMemo, useState } from 'react';

import type { DomainSubdomain, DomainSubdomainTrend, DashboardFilters } from '@/api/dashboard';
import { ArrowRightIcon, TrendUpIcon } from '@/components/ui/Icon';
import { Skeleton } from '@/components/ui/Skeleton';
import { TrendDelta } from '@/components/ui/TrendDelta';
import { useDomainSubdomainTrend } from '@/hooks/api/useDashboard';
import type { DomainMeta } from '@/data/mockPerformance';

import { visibleChartSections } from './chartSections';
import styles from './TrendsBanner.module.css';

interface TrendsBannerProps {
  domain: DomainMeta;
  filters: DashboardFilters;
  /** Opens the full DomainTrendModal drill-down. */
  onOpenTrends: () => void;
}

/** Movement below this (in points) is treated as noise, not a trend. */
const MOVE_THRESHOLD = 2;

/** Per-device memory of the disclosure state. */
const STORAGE_KEY = 'kai.lens.trendsBanner.expanded';

/** Short labels for the diverging strip; falls back to the API name. */
const SHORT_NAMES: Record<string, string> = {
  history: 'HPI',
  hpi: 'HPI',
  'physical exam': 'Exam',
  'physical_exam': 'Exam',
  exam: 'Exam',
  mdm: 'MDM',
  'vital signs': 'Vitals',
  vitals: 'Vitals',
  'labs/imaging': 'Labs',
  'labs_imaging': 'Labs',
  labs: 'Labs',
  'risk factors': 'Risk',
};

function shortName(sub: DomainSubdomain): string {
  const key = sub.name.trim().toLowerCase();
  return SHORT_NAMES[key] ?? SHORT_NAMES[sub.id.toLowerCase()] ?? sub.name;
}

/**
 * Trends call-out for the Lens page (replaces the header "Trends" pill).
 *
 * A disclosure card that sits between the header and the complaint list.
 * Collapsed, it is a single line: the headline sentence ("Physical Exam up 7,
 * HPI down 6") plus a chevron, so it never pushes the complaint list far down.
 * Expanded, it shows a diverging bar per section (delta over the selected
 * period), the overall score / rank / N, and an "Open trends" action that
 * launches the existing DomainTrendModal.
 *
 * Data: the same `/domains/:domain/subdomain-trend` request the modal makes,
 * with the same period filters, so the banner always describes the picker's
 * window and the modal is a zoom-in on what the banner already showed. When
 * the query is shared, React Query serves the modal from cache.
 *
 * The expanded / collapsed choice is remembered per device (localStorage).
 * First visit defaults to expanded so the feature is discovered; after that
 * the user's preference wins.
 */
export function TrendsBanner({ domain, filters, onOpenTrends }: TrendsBannerProps) {
  const query = useDomainSubdomainTrend(domain.key === 'rsq' ? domain.key : undefined, filters);
  const data = query.data;
  const panelId = useId();

  const [expanded, setExpanded] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved === null ? true : saved === '1';
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, expanded ? '1' : '0');
    } catch {
      /* private mode etc. -- preference just won't persist */
    }
  }, [expanded]);

  const summary = useMemo(() => summarise(data), [data]);

  // Domains other than RSQ have no sub-domain trends yet; render nothing
  // rather than an empty card.
  if (domain.key !== 'rsq') return null;

  if (query.isLoading) {
    return (
      <div className={styles.banner} aria-busy="true">
        <div className={styles.summaryRow}>
          <Skeleton variant="text" width="60%" />
        </div>
      </div>
    );
  }

  // Errors and empty windows fall back to a quiet one-liner that still
  // opens the modal (which renders its own error / empty state).
  const quiet = !summary || summary.movers.length === 0;

  return (
    <section
      className={`${styles.banner} ${quiet ? styles.bannerQuiet : styles.bannerActive}`}
      aria-label="Chart section trends for the selected period"
    >
      <button
        type="button"
        className={styles.summaryRow}
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={() => setExpanded((v) => !v)}
      >
        <span className={styles.kicker}>
          <TrendUpIcon width={14} height={14} />
          <span>Chart Section Trends</span>
          {data?.windowLabel && <span className={styles.window}>{data.windowLabel}</span>}
        </span>
        <span className={styles.headline}>
          {summary ? summary.headline : 'Section trends for this period'}
        </span>
        {summary && !quiet && (
          <span
            className={styles.countChip}
            title={`Moves under ${MOVE_THRESHOLD} points vs the prior period count as steady`}
          >
            {tallyLabel(summary)}
          </span>
        )}
        <span className={styles.chevron} aria-hidden="true" />
      </button>

      <div
        id={panelId}
        className={styles.panel}
        data-expanded={expanded ? 'true' : 'false'}
        aria-hidden={!expanded}
      >
        <div className={styles.panelInner}>
          {summary && !quiet ? (
            <DivergingStrip subs={summary.subs} />
          ) : (
            <p className={styles.quietText}>
              {summary && summary.subs.length > 0
                ? 'Nothing moved more than a point or two in this window. Widen the range to see movement.'
                : data?.reason ?? 'No section data for the active filters yet.'}
            </p>
          )}

          <div className={styles.footer}>
            {data?.score != null && (
              <span className={styles.footerStrong}>{data.score}% overall</span>
            )}
            {data?.rank != null && data?.cohortSize != null && (
              <span>#{data.rank} of {data.cohortSize}</span>
            )}
            {data?.chartCount != null && <span>N={data.chartCount.toLocaleString()}</span>}
            <button
              type="button"
              className={styles.openButton}
              onClick={onOpenTrends}
              aria-label={`Open ${domain.fullName} trend drill-down`}
              tabIndex={expanded ? 0 : -1}
            >
              <span>Open trends</span>
              <ArrowRightIcon width={14} height={14} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* Summary derivation
 * --------------------------------------------------------------------- */

interface Mover {
  sub: DomainSubdomain;
  delta: number;
}

interface Summary {
  headline: string;
  subs: DomainSubdomain[];
  movers: Mover[];
  ups: number;
  /** Sections whose move is inside the +/-MOVE_THRESHOLD band. */
  steady: number;
  downs: number;
}

/**
 * Signed period-over-period delta for a section. The API computes it as this
 * window vs the equal prior window (same definition as every other trend
 * chip); an explicit null means "no prior data", and is honoured as such.
 * The history-diff fallback only applies to a legacy payload with no `delta`
 * key at all.
 */
function deltaOf(sub: DomainSubdomain): number | null {
  if ('delta' in sub) return sub.delta ?? null;
  const pts = sub.history.filter((v): v is number => v != null);
  return pts.length >= 2 ? Math.round(pts[pts.length - 1] - pts[0]) : null;
}

function summarise(data: DomainSubdomainTrend | undefined): Summary | null {
  // Vitals is hidden from the section surfaces (K4); see chartSections.ts.
  const subs = data ? visibleChartSections(data.subs) : [];
  if (!data || subs.length === 0) return null;

  const movers: Mover[] = [];
  for (const sub of subs) {
    const d = deltaOf(sub);
    if (d != null && Math.abs(d) >= MOVE_THRESHOLD) movers.push({ sub, delta: d });
  }
  const ups = movers.filter((m) => m.delta > 0).length;
  const downs = movers.length - ups;
  // Everything with a delta that is not a counted move is "steady" (inside
  // the +/-MOVE_THRESHOLD band). Sections with no prior data are not tallied
  // at all. ups + steady + downs always equals the number of tallied bars.
  const withDelta = subs.filter((s) => deltaOf(s) != null).length;
  const steady = withDelta - movers.length;

  let headline: string;
  if (movers.length === 0) {
    headline = data.score != null ? `Holding steady at ${data.score}%` : 'Holding steady';
  } else {
    const riser = [...movers].filter((m) => m.delta > 0).sort((a, b) => b.delta - a.delta)[0];
    const faller = [...movers].filter((m) => m.delta < 0).sort((a, b) => a.delta - b.delta)[0];
    const parts: string[] = [];
    if (riser) parts.push(`${riser.sub.name} up ${riser.delta}`);
    if (faller) parts.push(`${faller.sub.name} down ${Math.abs(faller.delta)}`);
    headline = parts.join(', ');
  }

  return { headline, subs, movers, ups, steady, downs };
}

/**
 * Tally for the pill: only the non-zero buckets, in up / steady / down
 * order, so "1 up · 1 steady · 1 down" accounts for every bar on the strip.
 */
function tallyLabel(s: Summary): string {
  const parts: string[] = [];
  if (s.ups) parts.push(`${s.ups} up`);
  if (s.steady) parts.push(`${s.steady} steady`);
  if (s.downs) parts.push(`${s.downs} down`);
  return parts.join(' · ');
}

/* Diverging strip
 * --------------------------------------------------------------------- */

/**
 * One column per section: a bar rising or falling from a shared baseline
 * plus a signed label. Built in HTML (not a scaled SVG) so bar heights and
 * label sizes are fixed pixels on every viewport; on desktop the columns
 * simply spread out instead of growing.
 */
function DivergingStrip({ subs }: { subs: DomainSubdomain[] }) {
  const deltas = subs.map(deltaOf);
  const maxAbs = Math.max(
    MOVE_THRESHOLD,
    ...deltas.map((d) => (d == null ? 0 : Math.abs(d))),
  );
  const MAX_BAR = 22;

  return (
    <div
      className={styles.strip}
      role="img"
      aria-label={subs
        .map((s, i) => {
          const d = deltas[i];
          return `${s.name}: ${d == null ? 'no data' : d > 0 ? `up ${d}` : d < 0 ? `down ${-d}` : 'flat'}`;
        })
        .join('; ')}
    >
      {subs.map((s, i) => {
        const d = deltas[i];
        // A move inside the +/-MOVE_THRESHOLD band is "flat": it is not
        // counted in the "N up · N down" pill or the headline, so it must
        // not be painted green or red here either. The bar still shows the
        // small move (muted) so the picture stays honest.
        const none = d == null;
        const minor = !none && d !== 0 && Math.abs(d) < MOVE_THRESHOLD;
        const flat = none || d === 0;
        const up = !none && d > 0;
        const h = none || d === 0 ? 0 : Math.max(3, Math.round((Math.abs(d) / maxAbs) * MAX_BAR));
        const barClass = minor ? `${styles.bar} ${styles.barMinor}` : `${styles.bar} ${up ? styles.barUp : styles.barDown}`;
        return (
          <div key={s.id} className={styles.col}>
            <div className={styles.colUp}>
              {!flat && up && <span className={barClass} style={{ height: h }} />}
            </div>
            <div className={styles.colDown}>
              {flat ? (
                <span className={styles.barFlat} />
              ) : (
                !up && <span className={barClass} style={{ height: h }} />
              )}
            </div>
            {/* Label: counted moves show the signed delta; steady bars say
                so, with the small delta in the tooltip, so the strip and the
                tally use the same words. */}
            <span className={flat || minor ? styles.stripLabelMuted : styles.stripLabel}>
              {shortName(s)}{' '}
              <TrendDelta
                delta={d}
                direction={none ? 'flat' : minor || d === 0 ? 'flat' : up ? 'up' : 'down'}
                steadyLabel
                tone={!(flat || minor)}
                size="sm"
                title={
                  none
                    ? `${s.name}: no prior period to compare`
                    : `${s.name}: ${d >= 0 ? 'up' : 'down'} ${Math.abs(d)} pts vs prior period${minor ? ' (within the steady band)' : ''}`
                }
              />
            </span>
          </div>
        );
      })}
    </div>
  );
}
